import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { z } from 'zod'
import { useDashboardStore } from '@/store/dashboard'
import { useAuthStore } from '@/store/auth'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { RawResponseModal } from '@/components/RawResponseModal'
import { AddBrandModal } from '@/components/AddBrandModal'
import { BrandSettingsModal } from '@/components/BrandSettingsModal'
import { apiPost } from '@/lib/api'
import { TIER_BRAND_LIMITS, type ResultsResponse } from '@ekorank/shared'
import { BrandHeader } from '@/components/dashboard/BrandHeader'
import { KeyStatsCards } from '@/components/dashboard/KeyStatsCards'
import { MentionTrendChart } from '@/components/dashboard/MentionTrendChart'
import { ModelBreakdown } from '@/components/dashboard/ModelBreakdown'
import { PromptPerformance } from '@/components/dashboard/PromptPerformance'
import { CompetitorShareOfVoice } from '@/components/dashboard/CompetitorShareOfVoice'
import { SentimentBreakdown } from '@/components/dashboard/SentimentBreakdown'
import { InsightsPanel } from '@/components/dashboard/InsightsPanel'
import { ResultsTable } from '@/components/dashboard/ResultsTable'
import { FilterBar } from '@/components/dashboard/FilterBar'
import { Pagination } from '@/components/dashboard/Pagination'
import { CsvExportButton } from '@/components/dashboard/CsvExportButton'
import { deriveInsights } from '@/lib/insights'

const CheckResponseSchema = z.object({ ok: z.literal(true), data: z.object({ jobId: z.string() }) })

type ResultRow = ResultsResponse['results'][number]

interface ModalState {
  aiAnswer: string | null
  contextSnippet: string | null
  competitorMentions: string[]
  mentioned: boolean
  rankPosition: number | null
  sentiment: string | null
  keyword: string
  model: string
}

export default function Dashboard() {
  const {
    dashboard, insights, results, filters, loading, error,
    fetchDashboard, fetchInsights, fetchResults, setFilters, resetFilters,
  } = useDashboardStore()
  const user = useAuthStore((s) => s.user)
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null)
  const [modalResult, setModalResult] = useState<ModalState | null>(null)
  const [triggerLoading, setTriggerLoading] = useState(false)
  const [triggerFeedback, setTriggerFeedback] = useState<string | null>(null)
  const [addBrandOpen, setAddBrandOpen] = useState(false)
  const [settingsBrandId, setSettingsBrandId] = useState<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const creditsBalance = dashboard?.user.creditsBalance ?? 0
  const canTrigger = creditsBalance > 0
  const brandLimit = TIER_BRAND_LIMITS[user?.tier ?? 'free']
  const canAddBrand = dashboard ? dashboard.brands.length < brandLimit : false
  const selectedBrand = dashboard?.brands.find((b) => b.id === selectedBrandId) ?? null

  const insightCards = useMemo(() => {
    if (!selectedBrand || !insights) return []
    return deriveInsights({
      stats: selectedBrand.stats,
      perModel: insights.perModel,
      perPrompt: insights.perPrompt,
      competitors: insights.competitors,
      sentiment: insights.sentiment,
    })
  }, [selectedBrand, insights])

  const loadBrandData = useCallback((brandId: string) => {
    void fetchResults(brandId)
    void fetchInsights(brandId)
  }, [fetchResults, fetchInsights])

  const handleSelectBrand = useCallback((brandId: string) => {
    setSelectedBrandId(brandId)
    resetFilters()
    loadBrandData(brandId)
  }, [resetFilters, loadBrandData])

  const handleRunNow = useCallback(async () => {
    if (!selectedBrandId) return
    setTriggerLoading(true)
    const result = await apiPost(`/api/brands/${selectedBrandId}/check`, CheckResponseSchema, {})
    setTriggerLoading(false)
    if (!result.ok) {
      setTriggerFeedback(result.error)
      setTimeout(() => setTriggerFeedback(null), 5000)
      return
    }

    const triggerTime = Math.floor(Date.now() / 1000)
    let ticks = 0
    setTriggerFeedback('Checking for new results…')
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    const brandId = selectedBrandId
    pollIntervalRef.current = setInterval(() => {
      ticks++
      void fetchResults(brandId).then(() => {
        const latest = useDashboardStore.getState().results
        const hasNew = latest?.results.some((r) => r.checkedAt > triggerTime) ?? false
        if (hasNew || ticks >= 18) {
          clearInterval(pollIntervalRef.current!)
          pollIntervalRef.current = null
          setTriggerFeedback(hasNew ? 'Results updated.' : 'Check queued — results will appear shortly.')
          setTimeout(() => setTriggerFeedback(null), 4000)
          if (hasNew) {
            void fetchDashboard()
            void fetchInsights(brandId)
          }
        }
      })
    }, 5000)
  }, [selectedBrandId, fetchResults, fetchInsights, fetchDashboard])

  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current) }
  }, [])

  useEffect(() => {
    void fetchDashboard()
  }, [fetchDashboard])

  useEffect(() => {
    if (!selectedBrandId && dashboard?.brands[0]) {
      const firstId = dashboard.brands[0].id
      setSelectedBrandId(firstId)
      loadBrandData(firstId)
    }
  }, [dashboard, selectedBrandId, loadBrandData])

  const handleSortChange = (col: typeof filters.sort.col) => {
    if (!selectedBrandId) return
    const dir = filters.sort.col === col && filters.sort.dir === 'desc' ? 'asc' : 'desc'
    setFilters({ sort: { col, dir } }, selectedBrandId)
  }

  const handleRowClick = (r: ResultRow) => {
    setModalResult({
      aiAnswer: r.aiAnswer,
      contextSnippet: r.contextSnippet,
      competitorMentions: (r.competitors ?? []).map((c) => c.name),
      mentioned: r.mentioned,
      rankPosition: r.rankPosition,
      sentiment: r.sentiment ?? null,
      keyword: r.promptText,
      model: r.model,
    })
  }

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <>
      {modalResult && (
        <RawResponseModal
          aiAnswer={modalResult.aiAnswer}
          contextSnippet={modalResult.contextSnippet}
          competitorMentions={modalResult.competitorMentions}
          mentioned={modalResult.mentioned}
          rankPosition={modalResult.rankPosition}
          sentiment={modalResult.sentiment}
          keyword={modalResult.keyword}
          model={modalResult.model}
          onClose={() => setModalResult(null)}
        />
      )}
      {addBrandOpen && (
        <AddBrandModal
          onClose={(newBrandId) => {
            setAddBrandOpen(false)
            void fetchDashboard().then(() => {
              if (newBrandId) {
                setSelectedBrandId(newBrandId)
                loadBrandData(newBrandId)
              }
            })
          }}
        />
      )}
      {settingsBrandId && dashboard && (
        <BrandSettingsModal
          brandId={settingsBrandId}
          brandName={dashboard.brands.find((b) => b.id === settingsBrandId)?.name ?? ''}
          onClose={() => setSettingsBrandId(null)}
          onDeleted={() => {
            const deletedId = settingsBrandId
            setSettingsBrandId(null)
            void fetchDashboard().then(() => {
              if (selectedBrandId === deletedId) setSelectedBrandId(null)
            })
          }}
        />
      )}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/30 text-error">
            {error}
          </div>
        )}

        {dashboard?.brands.length === 0 && (
          <div className="text-center py-24">
            <p className="text-3xl font-bold text-white mb-4">No brands tracked yet</p>
            <p className="text-on-surface-variant mb-8">Set up your first brand to start tracking AI visibility.</p>
            <button
              onClick={() => setAddBrandOpen(true)}
              className="px-8 py-4 rounded-lg bg-gradient-to-r from-primary to-secondary text-on-primary-container font-bold inline-block hover:opacity-90 transition-opacity"
            >
              Add Your First Brand
            </button>
          </div>
        )}

        {dashboard && dashboard.brands.length > 0 && (
          <>
            <BrandHeader
              brands={dashboard.brands}
              selectedBrandId={selectedBrandId}
              onSelect={handleSelectBrand}
              onAdd={() => setAddBrandOpen(true)}
              onSettings={(id) => setSettingsBrandId(id)}
              canAddBrand={canAddBrand}
              onRunNow={() => void handleRunNow()}
              triggerLoading={triggerLoading}
              creditsBalance={creditsBalance}
              canTrigger={canTrigger}
            />
            {triggerFeedback && (
              <div className="mb-6 p-3 rounded-lg bg-surface-container border border-outline-variant/10 text-sm text-on-surface-variant">
                {triggerFeedback}
              </div>
            )}

            {selectedBrand && (
              <>
                <KeyStatsCards
                  stats={selectedBrand.stats}
                  modelsConfigured={selectedBrand.modelsConfigured}
                  modelsCovering={selectedBrand.modelsCovering}
                  lastCheckedAt={selectedBrand.lastCheckedAt}
                />

                <InsightsPanel cards={insightCards} />

                <MentionTrendChart data={selectedBrand.trendWide} />

                {insights && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-10">
                      <ModelBreakdown perModel={insights.perModel} />
                      <SentimentBreakdown sentiment={insights.sentiment} />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-10">
                      <PromptPerformance perPrompt={insights.perPrompt} />
                      <CompetitorShareOfVoice competitors={insights.competitors} />
                    </div>
                  </>
                )}

                <div className="bg-surface-container rounded-xl border border-outline-variant/10 overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-outline-variant/10 flex items-center justify-between gap-3">
                    <h3 className="text-sm font-mono text-outline uppercase tracking-widest">Check Results</h3>
                    {selectedBrandId && (
                      <CsvExportButton brandId={selectedBrandId} brandName={selectedBrand.name} filters={filters} />
                    )}
                  </div>
                  <FilterBar
                    filters={filters}
                    perPrompt={insights?.perPrompt ?? []}
                    onChange={(partial) => selectedBrandId && setFilters(partial, selectedBrandId)}
                    onReset={() => selectedBrandId && resetFilters(selectedBrandId)}
                  />
                  <ResultsTable
                    results={results}
                    loading={loading}
                    sort={filters.sort}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                  />
                  {results && (
                    <Pagination
                      page={results.pagination.page}
                      limit={results.pagination.limit}
                      total={results.pagination.total}
                      onPageChange={(page) => selectedBrandId && setFilters({ page }, selectedBrandId)}
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </>
  )
}
